import { z } from "@msuite/utils/lib/zod";
import { defineQuery } from "@rocicorp/zero";
import { zql } from "../zql";

/**
 * Work carries the plan's organisation, and is read by that organisation and by the one that owns
 * the crew it sits on — the two arms of its read policy. The second arm is what shows a partner the
 * work assigned to its own column on a board that is otherwise invisible to it.
 */
export const ticketAssignmentQueries = {
	byId: defineQuery(
		z.object({
			id: z.uuid(),
		}),
		({ args, ctx }) => {
			return zql.ticketAssignments
				.where("id", args.id)
				.where(({ or, cmp, exists }) =>
					or(
						cmp("orgId", "=", ctx.orgId),
						exists("crew", (crew) => crew.where("orgId", "=", ctx.orgId)),
					),
				)
				.one();
		},
	),
	byCrewId: defineQuery(
		z.object({
			crewId: z.uuid(),
		}),
		({ args, ctx }) => {
			// The ticket, its type and the project ride along so a guest board can render the work
			// on the caller's own column — the REST detail endpoints refuse a foreign caller, the
			// viewer's own type catalogue never holds a host's type, and the board is not allowed
			// to need either.
			//
			// None of the hops compares `ctx.orgId`, and this is the one place on the sync path
			// where that is the query rather than a gap. All three tables carry an organisation
			// and the replica carries the column, so the clause is available; writing it would
			// empty the guest column, because the work on a partner's crew, its type and the
			// project behind it are the *host's* rows by construction — the second arm of the root
			// exists precisely to reach them. Reachable rows stay bounded by that arm: nothing here
			// is enumerable beyond the work standing on a crew the caller owns.
			return zql.ticketAssignments
				.where("crewId", "=", args.crewId)
				.where(({ or, cmp, exists }) =>
					or(
						cmp("orgId", "=", ctx.orgId),
						exists("crew", (crew) => crew.where("orgId", "=", ctx.orgId)),
					),
				)
				.related("ticket", (ticket) => ticket.related("ticketType"))
				.related("project")
				.orderBy("ranking", "asc");
		},
	),
};
